import React, { Suspense, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { login, logout, removeCookie, getToken } from '../services/auth';
import { getUrlParam } from '../utils/params';
import { CoreApiContext } from '../contexts/coreApiContext';
import { isReactNativeWebView } from '../utils/isReactNativeWebView';

import authApi from '../services/authApi';
import Loading from '../components/atoms/Loading';

import Signin from '../pages/SignIn';
import Signup from '../pages/Signup';
import Verify from '../pages/Verify';
import AccountSettings from '../pages/AccountSettings';
import ForgotPassword from '../pages/ForgotPassword';
import MailStatus from '../pages/MailStatus';
import ConfirmEmail from '../pages/ConfirmEmail';
import ResetPassword from '../pages/ResetPassword';
import ConfirmedEmail from '../pages/ConfirmedEmail';
import MagicLinkVerify from '../pages/MagicLinkVerify';
import OAuthCallback from '../pages/OAuthCallback';

const SettingsRoute = ({ component: Component, ...rest }) => {
  const { getMe } = useContext(CoreApiContext);
  const queryParams = new URLSearchParams(rest.location.search);

  if (queryParams.get('access_token')) {
    login(queryParams.get('access_token'));
  }

  useEffect(() => {
    getMe().catch(() => {
      logout();
      window.location.assign('/accounts/sign_in');
    });
  }, []);

  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

const AuthRoute = ({ component: Component, ...rest }) => {
  useEffect(() => {
    authApi
      .get('/me')
      .then((meResponse) => {
        const authentication = meResponse.data;
        if (authentication.status === 'confirmation_expired') {
          logout();
          if (window.ReactNativeWebView) {
            const message = {
              status: 200,
              data: {
                logged: false,
              },
            };

            window.ReactNativeWebView.postMessage(JSON.stringify(message));
          }
        } else if (isReactNativeWebView()) {
          // authApiToken is httpOnly — JS cannot read it. Use the token
          // persisted in localStorage by login()/loginUser().
          const token = getToken();
          if (token) {
            const message = {
              type: 'AUTH_SUCCESS',
              data: {
                token,
              },
            };

            window.ReactNativeWebView.postMessage(JSON.stringify(message));
          }
        } else {
          window.location.replace(process.env.REACT_APP_FULL_URL);
        }
      })
      .catch((_error) => {
        if (window.ReactNativeWebView) {
          const message = {
            status: 200,
            data: {
              logged: false,
            },
          };

          window.ReactNativeWebView.postMessage(JSON.stringify(message));
        }
      });
  }, []);

  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

const Routes = () => {
  const getAccessToken = getUrlParam('access_token');

  useEffect(() => {
    removeCookie();
    if (getAccessToken === 'true') logout();
  }, []);

  return (
    <>
      <Router>
        <Suspense fallback={<Loading />}>
          <Switch>
            <AuthRoute exact path="/">
              <Signin />
            </AuthRoute>
            <AuthRoute exact path="/accounts/sign_in">
              <Signin />
            </AuthRoute>
            <Route exact path="/start">
              <Signup />
            </Route>
            <AuthRoute exact path="/accounts/verify">
              <Verify />
            </AuthRoute>
            <SettingsRoute exact path="/accounts/settings">
              <AccountSettings />
            </SettingsRoute>
            <Route exact path="/accounts/forgot_password">
              <ForgotPassword />
            </Route>
            <Route exact path="/accounts/reset_password">
              <ResetPassword />
            </Route>
            <Route exact path="/accounts/forgot_password/mail_sent">
              <MailStatus status="sent" />
            </Route>
            <Route exact path="/accounts/forgot_password/mail_resent">
              <MailStatus status="resent" />
            </Route>
            <Route exact path="/accounts/confirmation/new">
              <ConfirmEmail />
            </Route>
            <Route exact path="/accounts/confirmed">
              <ConfirmedEmail />
            </Route>
            <Route exact path="/accounts/magic_link_verify">
              <MagicLinkVerify />
            </Route>
            <Route exact path="/oauth/callback">
              <OAuthCallback />
            </Route>
            <AuthRoute path="*">
              <Signin />
            </AuthRoute>
          </Switch>
        </Suspense>
      </Router>
    </>
  );
};

export default Routes;
